// Core packages
import { Injectable } from "@angular/core";

// Third party packages
import { Observable } from "rxjs";
import { catchError } from "rxjs/operators";

// Custom packages
import { ListApiResponseV2 } from "src/app/shared/interfaces/_base/api-response.interface";
import { ApiService } from "src/app/shared/services/api.service";
import { HelperService } from "src/app/shared/services/helper.service";

@Injectable({
  providedIn: "root",
})
export class AccessLogsService {
  /**
   * Class constructor
   */
  constructor(
    private apiService: ApiService,
    private helperService: HelperService
  ) {}

  /**
   * Create accessLog with given role
   *
   * @since 1.0.0
   *
   * @param data New accessLog's data
   */
  create(
    roleType: string,
    companyId: string,
    type?: string,
    firstName?: string,
    lastName?: string,
    companyName?: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "POST",
      `access-logs`,
      { roleType, companyId, type, firstName, lastName, companyName },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Create accessLog with given role per QRcode area
   *
   * @since 1.0.0
   *
   * @param data New accessLog's data
   */
  Qrcode(
    roleType: string,
    type?: string,
    firstName?: string,
    lastName?: string,
    companyName?: string,
    companyId?: string
  ): Observable<any> {
    const chosenRole = {
      roleType,
      company: companyId
    }
    return this.apiService.apiCall(
      "POST",
      `access-logs/qrcode`,
      { chosenRole, companyId, type, firstName, lastName, companyName },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }
  /**
   * Get access logs list for given query params
   *
   * @since 1.0.0
   *
   * @param [start] Query left offset
   * @param [limit] Results number
   * @param [sort] Sorting field name
   * @param [sortVersus] Sorting versus. Can be 'asc' or 'desc'
   * @param [search] Filtering object with fildering data string
   */
  getList(
    start?: number,
    limit?: number,
    sort?: string,
    sortVersus?: string,
    search?: any
  ): Observable<ListApiResponseV2> {
    const params: any = { start, limit, sort, sortVersus };
    if (search) {
      Object.keys(search).forEach((key) => {
        const newParamKey = `search[${key}]`;
        const newParamValue = search[key];
        params[newParamKey] = newParamValue;
      });
    }
    return this.apiService.apiCall(
      "GET",
      "access-logs",
      params,
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }

  /**
   * Get an exportation file of all compananies
   *
   * @since 1.0.0
   */
  getExport(companyId: string): Observable<any> {
    if (companyId) {
      return this.apiService
        .apiCall(
          "GET",
          `access-logs/export/${companyId}`,
          {},
          { responseType: "blob" },
          undefined,
          undefined,
          "v2"
        )
        .pipe(
          catchError((err) => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    } else {
      return this.apiService
        .apiCall(
          "GET",
          "access-logs/export",
          {},
          { responseType: "blob" },
          undefined,
          undefined,
          "v2"
        )
        .pipe(
          catchError((err) => {
            return this.helperService.parseErrorBlob(err);
          })
        );
    }
  }

  /**
   * Update accessLog with given role
   *
   * @since 1.0.0
   *
   * @param accessId ID of accessLog to update
   * @param data New accessLog's data
   */
  update(
    accessId: string,
    roleType: string,
    companyId: string
  ): Observable<any> {
    return this.apiService.apiCall(
      "PUT",
      `access-logs/${accessId}`,
      { roleType, companyId },
      undefined,
      undefined,
      undefined,
      "v2"
    );
  }
}
